import { Link, useNavigate } from "react-router";
import { useState } from "react";
import styled from "styled-components";

import CuratedWalletList from "./CuratedWalletList";
import Seo from "./Seo";
import { routeMeta } from "./routeMeta";
import PageHeading from "./PageHeading";
import { PageContainer } from "./sharedStyleds";
import { randomCuratedWallet } from "./curatedWalletData";
import { idShapeOf, verifyId } from "./btcId";

const HISTORY_STORAGE_KEY = "btcQueryHistory";

const BtcSearchPage = PageContainer;

// The form is the first thing on the page most people will try, and it was the
// last part of the site never given a pass: a terminal prompt for a label, a
// note about pipe-delimited addresses above the button, and "I'm Feeling Lucky"
// next to Submit. What it does has not changed - one address, several separated
// by "|", or a transaction hash - only what it looks like and what it says when
// the input cannot be looked up.
const Form = styled.form`
  max-width: 46rem;
  margin-bottom: 2rem;
`;

const Field = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Input = styled.input`
  flex: 1 1 22rem;
  min-width: 0;
  /* terminal.css sizes form controls off its own base; these two have to match
     or the button sits a pixel proud of the field */
  height: 2.6rem;
  margin: 0;
  font-family: inherit;
`;

const Submit = styled.button`
  height: 2.6rem;
  margin: 0;
  padding: 0 1.4rem;
`;

const Hint = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  color: #a3abba;
`;

// Red, and announced: a screen reader gets told the lookup was refused rather
// than the field silently doing nothing.
const Problem = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.9rem;
  color: #ff3c74;
`;

const Lucky = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: #62c4ff;
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
`;

const RecentSearches = styled.h3`
  margin: 1.6rem 0 0.2rem;
  font-size: 1rem;
`;

const RecentList = styled.ul`
  margin-top: 0;
  font-size: 0.9rem;
  word-break: break-all;
`;

const BtcSearch = () => {
  const [query, setQuery] = useState("");
  const [problem, setProblem] = useState(null);
  const navigate = useNavigate();

  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HISTORY_STORAGE_KEY)) || [];
    } catch {
      // private browsing, or something else wrote nonsense under the key
      return [];
    }
  });

  const remember = (entry) => {
    const history = [entry, ...recentSearches.filter((x) => x !== entry)].slice(0, 8);
    setRecentSearches(history);
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch {
      // a lost history is not worth failing the lookup over
    }
  };

  // Saying which of the two it is, because the fix differs: a checksum failure
  // is a typo in something that was nearly right, and anything else was never an
  // address at all.
  const describe = (bad, total) => {
    if (total > 1) {
      return `Could not read ${bad.length} of the ${total} entries: ${bad.join(", ")}`;
    }
    return idShapeOf(bad[0]) === "wallet"
      ? "That is the right shape for a Bitcoin address, but its checksum does not match - there is a typo in it somewhere."
      : "That is not a Bitcoin address or transaction hash. Addresses start with 1, 3 or bc1, and a transaction hash is 64 hexadecimal characters.";
  };

  // Checked here rather than after navigating. The page it would have gone to
  // now answers "page not found" for a bad id, which is right but late: the
  // field the visitor typed in is where a typo is easiest to fix.
  const submit = (event) => {
    event.preventDefault();
    const entry = query.trim();
    if (!entry) return;

    const parts = entry.split("|").map((part) => part.trim()).filter(Boolean);
    const bad = parts.filter((part) => !verifyId(part));

    if (bad.length > 0) {
      setProblem(describe(bad, parts.length));
      return;
    }

    setProblem(null);
    remember(parts.join("|"));
    navigate(`/btc-explorer/${parts.join("|")}`);
  };

  return (
    <BtcSearchPage>
      <Seo {...routeMeta["/btc-explorer"]} path="/btc-explorer" />
      <PageHeading meta={routeMeta["/btc-explorer"]} />

      <Form onSubmit={submit}>
        <label htmlFor="btcAddress">Wallet address or transaction hash</label>
        <Field>
          <Input
            id="btcAddress"
            name="btcAddress"
            type="text"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
            placeholder="bc1qm34lsc65zpw79lxes69zkqmk6ee3ewf0j77s3h"
            aria-describedby="btcAddressHint"
            aria-invalid={problem ? "true" : undefined}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              if (problem) setProblem(null);
            }}
          />
          <Submit className="btn btn-primary" type="submit">
            Look up
          </Submit>
        </Field>
        {problem ? (
          <Problem role="alert">{problem}</Problem>
        ) : (
          <Hint id="btcAddressHint">
            Several addresses at once: separate them with <code>|</code>. Or{" "}
            <Lucky type="button" onClick={() => navigate(`/btc-explorer/${randomCuratedWallet().address}`)}>
              open a notable wallet
            </Lucky>
            .
          </Hint>
        )}

        {recentSearches.length > 0 && (
          <>
            <RecentSearches>Recent lookups</RecentSearches>
            <RecentList>
              {recentSearches.map((entry) => (
                <li key={entry}>
                  {/* an absolute path: a relative one resolved against whatever
                      route happened to be current */}
                  <Link to={`/btc-explorer/${entry}`}>{entry}</Link>
                </li>
              ))}
            </RecentList>
          </>
        )}
      </Form>

      <CuratedWalletList />
    </BtcSearchPage>
  );
};

export default BtcSearch;
